import {
  eventsQueryKeys,
  getEvent,
  useGetEventFormQuestionsForMultipleEventsQuery,
  useGetEventQuery,
} from "@ac-expo/api";
import {
  CreateTransactionResponseData,
  ValidateTransactionResponse,
} from "@ac-expo/api-types";
import { useEmbedTicketing } from "@ac-expo/hooks";
import {
  Loading,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@ac-expo/ui-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import EmbedFooter from "@web-app/src/components/Embed/Footer";
import { GroupUnlockedSession } from "@web-app/src/components/Ticket/GroupUnlock";
import {
  BuyerInformationsForm,
  BuyerInformationsSchema,
} from "@web-app/src/schema/embedTicketing.schema";
import { observer } from "mobx-react-lite";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useEmbedGuestSession } from "./embed-guest-session";
import { getInformationsForConsent } from "./utils";
import { EmbedCartComponent } from "./EmbedCartComponent";
import { EmbedConfirmationComponent } from "./EmbedConfirmationComponent";
import { EmbedFormComponent } from "./EmbedFormComponent";
import { EmbedInformationsComponent } from "./EmbedInformationsComponent";
import EmbedPaymentComponent from "./EmbedPaymentComponent";

export type STEPS =
  | "CART"
  | "INFORMATIONS"
  | "PAYMENT"
  | "CONFIRMATION"
  | "FORM";

type EmbedTicketingProps = {
  eventId: string;
};

export const EmbedTicketing = observer(({ eventId }: EmbedTicketingProps) => {
  const searchParams = useSearchParams();
  const isDisabled = searchParams.get("isDisabled") === "true";

  const [step, setStep] = useState<STEPS>("CART");
  const [paymentLink, setPaymentLink] =
    useState<CreateTransactionResponseData | null>(null);
  const [result, setResult] = useState<ValidateTransactionResponse | null>(
    null
  );
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [isTransactionComplete, setIsTransactionComplete] = useState(false);
  // Order-scoped token returned by POST /ticketing: the embed runs in a
  // cross-site iframe where no session cookie is sent, and registered buyers
  // never receive a guest session, so the form answers are authorised per order
  const [formAnswersToken, setFormAnswersToken] = useState<string | null>(null);

  const queryClient = useQueryClient();
  // Group unlock (ADR 0005): the guest session token is kept per event and sent as
  // Bearer, the iframe never receiving the cross-site session cookie
  const { guestSession, saveGuestSession } = useEmbedGuestSession(eventId);

  const {
    data: event,
    isLoading,
    isError,
  } = useGetEventQuery(eventId, {
    accessToken: guestSession?.accessToken,
  });

  const handleGroupUnlocked = useCallback(
    async (session: GroupUnlockedSession) => {
      saveGuestSession(session);

      // The event filter runs per session: the refetch is what reveals the Group tickets
      await queryClient.fetchQuery({
        queryKey: eventsQueryKeys.getAsGuest(eventId, session.accessToken),
        queryFn: () => getEvent(eventId, session.accessToken),
        staleTime: 0,
      });
    },
    [eventId, queryClient, saveGuestSession]
  );
  // The batch endpoint is public, unlike GET /:eventId/questions which 401s
  // for the anonymous visitors browsing the embed
  const eventIds = useMemo(() => [eventId], [eventId]);
  const { data: batchFormQuestions, isLoading: isLoadingFormQuestions } =
    useGetEventFormQuestionsForMultipleEventsQuery(eventIds);
  const eventFormQuestionsOnEvent = batchFormQuestions?.[eventId]?.questions;

  const {
    localCart,
    buyableTicketTypes,
    buyableBadgeTypes,
    buyableActivityTicketTypes,
  } = useEmbedTicketing({ event });

  const hasEventForm =
    eventFormQuestionsOnEvent && eventFormQuestionsOnEvent.length > 0;

  // Activity tickets are excluded, as in the web-app cart
  const formCount = useMemo(
    () =>
      [...buyableTicketTypes, ...buyableBadgeTypes].reduce(
        (count, entry) => count + localCart.getTotalQuantity(entry.itemKey),
        0
      ),
    [buyableTicketTypes, buyableBadgeTypes, localCart]
  );

  const activityTicketsSelected = useMemo(
    () =>
      buyableActivityTicketTypes
        .map((entry) => ({
          id: entry.entryType.id,
          quantity: localCart.getTotalQuantity(entry.itemKey),
        }))
        .filter((item) => item.quantity > 0),
    [buyableActivityTicketTypes, localCart]
  );

  const { organiserId, structureIds } = useMemo(() => {
    if (!event) return { organiserId: undefined, structureIds: [] };
    return getInformationsForConsent(event, activityTicketsSelected);
  }, [event, activityTicketsSelected]);

  const transactionForm = useForm<BuyerInformationsForm>({
    resolver: zodResolver(BuyerInformationsSchema),
    defaultValues: {
      organisers: organiserId ? [{ id: organiserId, hasConsented: false }] : [],
      structures: structureIds.map((id) => ({ id, hasConsented: false })),
    },
  });

  useEffect(() => {
    setTotalPrice(localCart.totalPricing.total);
    setTotalDiscount(localCart.totalPricing.discount);
  }, [localCart.totalPricing.total, localCart.totalPricing.discount]);

  useEffect(() => {
    if (organiserId) {
      transactionForm.setValue("organisers", [
        { id: organiserId, hasConsented: false },
      ]);
    }
    if (structureIds.length > 0) {
      transactionForm.setValue(
        "structures",
        structureIds.map((id) => ({ id, hasConsented: false }))
      );
    }
  }, [organiserId, structureIds, transactionForm]);

  if (isLoading || isLoadingFormQuestions) {
    return <Loading />;
  }

  if (isError || !event) {
    return <h2>Erreur récupération événement</h2>;
  }

  if (!event.isTicketingEnabled || !event.isTicketingAvailable) {
    return <h2>La billetterie n&apos;est pas disponible pour cet événement</h2>;
  }

  return (
    <div className="flex min-h-[500px] flex-col">
      <Tabs className="flex-1 p-4" value={step}>
        <TabsList className="w-full">
          <TabsTrigger
            className="grow flex-col gap-1 !opacity-100"
            value="CART"
            disabled
          >
            Panier
          </TabsTrigger>
          <TabsTrigger
            className="grow flex-col gap-1 !opacity-100"
            value="INFORMATIONS"
            disabled
          >
            Coordonnées
          </TabsTrigger>
          {hasEventForm && (
            <TabsTrigger
              className="grow flex-col gap-1 !opacity-100"
              value="FORM"
              disabled
            >
              Formulaire
            </TabsTrigger>
          )}
          <TabsTrigger
            className="grow flex-col gap-1 !opacity-100"
            value="PAYMENT"
            disabled
          >
            Paiement
          </TabsTrigger>
          <TabsTrigger
            className="grow flex-col gap-1 !opacity-100"
            value="CONFIRMATION"
            disabled
          >
            Confirmation
          </TabsTrigger>
        </TabsList>
        <div className="pt-4">
          <TabsContent value="CART">
            <EmbedCartComponent
              event={event}
              onGroupUnlocked={handleGroupUnlocked}
              localCart={localCart}
              buyableTicketTypes={buyableTicketTypes}
              buyableBadgeTypes={buyableBadgeTypes}
              buyableActivityTicketTypes={buyableActivityTicketTypes}
              setStep={setStep}
              totalPrice={totalPrice}
              totalDiscount={totalDiscount}
              isDisabled={isDisabled}
            />
          </TabsContent>
          <TabsContent value="INFORMATIONS">
            <EmbedInformationsComponent
              setStep={setStep}
              event={event}
              localCart={localCart}
              buyableTicketTypes={buyableTicketTypes}
              buyableBadgeTypes={buyableBadgeTypes}
              buyableActivityTicketTypes={buyableActivityTicketTypes}
              transactionForm={transactionForm}
              setPaymentLink={setPaymentLink}
              setTotal={setTotalPrice}
              setTotalDiscount={setTotalDiscount}
              totalPrice={totalPrice}
              totalDiscount={totalDiscount}
              setResult={setResult}
              setIsTransactionComplete={setIsTransactionComplete}
              eventFormQuestionsOnEvent={eventFormQuestionsOnEvent}
              setFormAnswersToken={setFormAnswersToken}
              guestSession={guestSession}
            />
          </TabsContent>
          {hasEventForm && (
            <TabsContent value="FORM">
              <EmbedFormComponent
                setStep={setStep}
                eventId={eventId}
                isTransactionComplete={isTransactionComplete}
                eventFormQuestionsOnEvent={eventFormQuestionsOnEvent}
                formAnswersToken={formAnswersToken}
                formCount={formCount}
              />
            </TabsContent>
          )}
          <TabsContent value="PAYMENT">
            <EmbedPaymentComponent
              setStep={setStep}
              paymentLink={paymentLink}
              setPaymentLink={setPaymentLink}
              setResult={setResult}
              eventSlug={event.slug}
            />
          </TabsContent>
          <TabsContent value="CONFIRMATION">
            <EmbedConfirmationComponent
              setStep={setStep}
              resetPreviousForms={() => {
                transactionForm.reset();
                localCart.resetCart();
                setPaymentLink(null);
                setResult(null);
                setIsTransactionComplete(false);
                setFormAnswersToken(null);
              }}
              result={result}
              eventSlug={event.slug}
            />
          </TabsContent>
        </div>
      </Tabs>
      <EmbedFooter />
    </div>
  );
});
